<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const emit = defineEmits(['close', 'detected']);

const { t } = useI18n();

const videoRef = ref(null);
const scannerControls = ref(null);
const scanError = ref('');
const isStarting = ref(true);
const lastDetectedValue = ref('');

const RETAIL_BARCODE_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
  BarcodeFormat.QR_CODE,
];

/**
 * Builds ZXing hints for jewelry and retail labels, where EAN/UPC and Code 128
 * are more common than QR codes.
 *
 * @returns {Map<unknown, unknown>} ZXing decode hints.
 */
function buildScannerHints() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, RETAIL_BARCODE_FORMATS);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return hints;
}

const scannerStatusLabel = computed(() => {
  if (scanError.value === 'PRODUCTS.SCANNER.ERROR_INSECURE_CONTEXT') {
    return t('PRODUCTS.SCANNER.ERROR_INSECURE_CONTEXT');
  }

  if (scanError.value === 'PRODUCTS.SCANNER.ERROR_UNSUPPORTED') {
    return t('PRODUCTS.SCANNER.ERROR_UNSUPPORTED');
  }

  if (scanError.value === 'PRODUCTS.SCANNER.ERROR_PERMISSION') {
    return t('PRODUCTS.SCANNER.ERROR_PERMISSION');
  }

  if (scanError.value === 'PRODUCTS.SCANNER.ERROR_NO_CAMERA') {
    return t('PRODUCTS.SCANNER.ERROR_NO_CAMERA');
  }

  if (scanError.value === 'PRODUCTS.SCANNER.ERROR_GENERIC') {
    return t('PRODUCTS.SCANNER.ERROR_GENERIC');
  }

  if (isStarting.value) {
    return t('PRODUCTS.SCANNER.STARTING');
  }

  return t('PRODUCTS.SCANNER.READY');
});

/**
 * Checks whether the current origin exposes the modern camera API used by
 * ZXing's browser scanner.
 *
 * @returns {boolean} Whether getUserMedia is available.
 */
function hasCameraApi() {
  return Boolean(window.navigator?.mediaDevices?.getUserMedia);
}

/**
 * Returns the most useful message when Safari or another browser hides the
 * camera API before ZXing can request permission.
 *
 * @returns {string} Translation key.
 */
function unavailableCameraApiKey() {
  if (window.isSecureContext === false) {
    return 'PRODUCTS.SCANNER.ERROR_INSECURE_CONTEXT';
  }

  return 'PRODUCTS.SCANNER.ERROR_UNSUPPORTED';
}

/**
 * Converts browser and ZXing camera failures into stable translation keys.
 *
 * @param {unknown} error Scanner start or runtime error.
 * @returns {string} Translation key.
 */
function scannerErrorKey(error) {
  const errorName = error?.name || '';

  if (!hasCameraApi()) {
    return unavailableCameraApiKey();
  }

  if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
    return 'PRODUCTS.SCANNER.ERROR_PERMISSION';
  }

  if (errorName === 'NotFoundError' || errorName === 'OverconstrainedError') {
    return 'PRODUCTS.SCANNER.ERROR_NO_CAMERA';
  }

  return 'PRODUCTS.SCANNER.ERROR_GENERIC';
}

/**
 * Stops the active ZXing scanner controls and releases the video stream.
 *
 * @returns {void}
 */
function stopScanner() {
  scannerControls.value?.stop?.();
  scannerControls.value = null;

  const stream = videoRef.value?.srcObject;
  stream?.getTracks?.().forEach(track => track.stop());
  if (videoRef.value) videoRef.value.srcObject = null;
}

/**
 * Emits the first detected barcode and stops the scanner to prevent duplicate
 * alert flow transitions.
 *
 * @param {string} barcode Detected barcode or QR payload.
 * @returns {void}
 */
function handleDetectedBarcode(barcode) {
  const normalizedBarcode = String(barcode || '').trim();
  if (!normalizedBarcode || lastDetectedValue.value) return;

  lastDetectedValue.value = normalizedBarcode;
  emit('detected', normalizedBarcode);
  stopScanner();
}

/**
 * Starts continuous ZXing scanning on the current camera.
 *
 * @returns {Promise<void>}
 */
async function startScanner() {
  if (!videoRef.value) return;

  isStarting.value = true;
  scanError.value = '';

  if (!hasCameraApi()) {
    scanError.value = unavailableCameraApiKey();
    isStarting.value = false;
    return;
  }

  try {
    const codeReader = new BrowserMultiFormatReader(buildScannerHints());
    scannerControls.value = await codeReader.decodeFromVideoDevice(
      undefined,
      videoRef.value,
      result => {
        if (result) handleDetectedBarcode(result.getText());
      }
    );
  } catch (error) {
    scanError.value = scannerErrorKey(error);
  } finally {
    isStarting.value = false;
  }
}

/**
 * Closes the drawer after releasing any active camera stream.
 *
 * @returns {void}
 */
function closeScanner() {
  stopScanner();
  emit('close');
}

onMounted(startScanner);
onBeforeUnmount(stopScanner);
</script>

<template>
  <section
    data-testid="barcode-scanner-drawer"
    class="flex h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl border border-n-weak bg-n-solid-1 shadow-xl lg:h-full lg:max-w-md lg:rounded-none"
  >
    <header
      class="flex items-center justify-between gap-3 border-b border-n-weak px-4 py-4"
    >
      <div class="min-w-0">
        <h2 class="truncate text-base font-semibold text-n-slate-12">
          {{ t('PRODUCTS.SCANNER.TITLE') }}
        </h2>
        <p class="truncate text-xs text-n-slate-10">
          {{ t('PRODUCTS.SCANNER.SUBTITLE') }}
        </p>
      </div>
      <Button
        data-testid="scanner-close"
        icon="i-lucide-x"
        color="slate"
        size="sm"
        variant="ghost"
        class="!rounded-full bg-n-alpha-2"
        @click="closeScanner"
      />
    </header>

    <div class="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
      <div class="relative overflow-hidden rounded-xl bg-n-slate-12">
        <video
          ref="videoRef"
          data-testid="scanner-video"
          class="aspect-[3/4] w-full object-cover md:aspect-[4/5]"
          muted
          playsinline
        />
        <div
          class="pointer-events-none absolute inset-0 flex items-center justify-center p-8"
        >
          <div
            class="h-28 w-full max-w-72 rounded-xl border-2 border-white/90 shadow-[0_0_0_999px_rgba(0,0,0,0.35)]"
          />
        </div>
      </div>

      <div class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-3">
        <div class="flex items-start gap-2">
          <Icon
            :icon="scanError ? 'i-lucide-circle-alert' : 'i-lucide-scan-line'"
            class="mt-0.5 size-4 shrink-0 text-n-slate-11"
          />
          <p class="text-sm text-n-slate-11">
            {{ scannerStatusLabel }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
